// Mirrors src/intelligence/baselines (anomaly.ts, intraday.ts, index.ts) in the collector.
export type AnomalyStatus = 'in' | 'near' | 'out' | 'insufficient';
export type AnomalyTrack = 'today' | 'past';
// N days with no end = the last N days ending today, today projected to a full day; with an end = complete days.
export type PeriodSpec = { kind: 'today' } | { kind: 'days'; days: number; end?: 'yesterday' };
export type PipelineKind = 'channel' | 'store' | 'action' | 'pixel';
export const PIPELINE_KIND_LABELS: Record<PipelineKind, string> = { channel: 'ad channel', store: 'store, all channels', action: 'conversion action', pixel: 'Meta pixel' };
// The one question each tab and each kind of card answers. Ad platforms & tracking asks whether the
// data is right (the counts agree with each other); Products & campaigns asks whether the money is
// right. Business rows on a tracking card (CPC, ROAS …) are context: money is judged on the other tab.
export const TAB_QUESTIONS = {
  subjects: { question: 'Is the money right?', blurb: 'Each card is a product family or a campaign, judged on its profit, revenue and efficiency against its own usual. Losses and collapses show here.' },
  pipelines: { question: 'Is the data right?', blurb: "Each card checks that the counting still counts: ad platforms, Triple Whale, Shopify and the pixels agreeing with each other. A broken tag, feed or setting shows here; business moves (CPC, ROAS …) are shown for context and judged on Products & campaigns." },
} as const;
export const CARD_QUESTIONS: Record<PipelineKind | 'product' | 'campaign', string> = {
  store: 'do Triple Whale, Shopify and the product report agree?',
  channel: 'does the platform count what Triple Whale sees?',
  action: 'are the pixel and server copies arriving, and counted?',
  pixel: 'are browser and server purchases reaching Meta?',
  product: 'profit, orders and revenue against its usual',
  campaign: 'profit and efficiency against its usual',
};
export const PERIOD_MAX_DAYS = 14;
// The default read per tab. Products & campaigns: yesterday, the latest complete day, so today's
// partial day never sets a status. Ad platforms & tracking: the last 3 settled days — its counting
// rows need 20 purchases a period, and a single day of a smaller store (CA ~8–15, Noonlash ~5–8
// a day) reads nothing.
export const DEFAULT_PERIODS: Record<'subjects' | 'pipelines', PeriodSpec> = {
  subjects: { kind: 'days', days: 1, end: 'yesterday' },
  pipelines: { kind: 'days', days: 3, end: 'yesterday' },
};
export const PERIOD_OPTIONS: Array<{ key: string; label: string; spec: PeriodSpec }> = [
  { key: 'today', label: 'Today so far', spec: { kind: 'today' } },
  { key: '1@yesterday', label: 'Yesterday', spec: { kind: 'days', days: 1, end: 'yesterday' } },
  { key: '3@yesterday', label: 'Last 3 settled days', spec: { kind: 'days', days: 3, end: 'yesterday' } },
];
export const periodKey = (p: PeriodSpec): string => (p.kind === 'today' ? 'today' : p.end ? `${p.days}@${p.end}` : String(p.days));
export const parsePeriodKey = (k: string | null): PeriodSpec | null => {
  if (k === 'today') return { kind: 'today' };
  const [n, end] = (k ?? '').split('@');
  const days = Number(n);
  if (!(Number.isInteger(days) && days >= 1 && days <= PERIOD_MAX_DAYS)) return null;
  if (end && end !== 'yesterday') return null;
  return end ? { kind: 'days', days, end: 'yesterday' } : { kind: 'days', days };
};
export const METHODS = ['robust_z', 'weekday_z', 'iqr', 'percentile', 'ewma'] as const;
export type AnomalyMethod = (typeof METHODS)[number];
export const METHOD_LABELS: Record<AnomalyMethod, { short: string; long: string }> = {
  robust_z: { short: 'z', long: 'Robust z: distance from the median in MAD spreads (primary)' },
  weekday_z: { short: 'wd', long: 'Same-weekday z: the same, against the same weekday only' },
  iqr: { short: 'iqr', long: 'Tukey fences: beyond the quartiles by a multiple of the interquartile range set by the sensitivity (Strict 1.35× near / 2.47× out; Standard 0.98× / 1.72×)' },
  percentile: { short: '%', long: 'Percentile: rank within the window, tails set by the sensitivity (Strict: near <10th / >90th, out <2.5th / >97.5th; Standard: 15.6% / 4.4% tails)' },
  ewma: { short: 'ewma', long: 'EWMA: distance from a recent-weighted level in recent-weighted spreads' },
};
// Peer sets a grain can be compared against (mirrors peerSetsFor in the collector). Off by default.
export const PEER_SETS_FOR: Record<'campaign' | 'product', string[]> = { campaign: ['store_channel', 'family_campaigns'], product: ['store_families'] };
export const PEER_SET_LABELS: Record<string, string> = {
  store_channel: 'same store + channel',
  family_campaigns: 'same family, other campaigns',
  store_families: "store's other families",
  store_pages: "store's other pages",
};

export interface Bounds { nearLow: number | null; nearHigh: number | null; outLow: number | null; outHigh: number | null }
export interface MethodResult { score: number | null; status: AnomalyStatus; detail: string; samples: number; bounds?: Bounds; path?: Array<{ end: string; level: number; nearLow: number; nearHigh: number; outLow: number; outHigh: number }> }
export interface PeerAnomaly { set: string; key: string; share: number | null; representative: boolean; peerValue: number | null; score: number | null; status: AnomalyStatus; detail: string }
// The metric's general drift across the window drawn (mirrors trend.ts): Theil–Sen for the size, Mann–Kendall for
// whether it is consistent. `clear` only when both the consistency (|z| ≥ near) and the size (≥ 10% of the level) hold.
export interface Trend { direction: 'up' | 'down' | 'flat'; clear: boolean; z: number; changeRel: number; fit: { start: { date: string; value: number }; end: { date: string; value: number } }; samples: number; days: number; detail: string }
// Metrics whose direction is scale, not quality: a trend on them is shown without a good / bad colour (mirrors SCALE_METRICS in the collector).
export const SCALE_METRICS = new Set(['spend_per_day', 'clicks_per_day', 'impressions_per_day', 'visit_sessions_per_day']);
export interface MetricAnomaly {
  key: string;
  label: string;
  unit: 'ratio' | 'rate' | 'money' | 'money_per_day' | 'count_per_day';
  higherIsBetter: boolean;
  scale: 'log' | 'abs';
  value: number | null;
  soFar: number | null;
  projection: string[] | null; // how today's so-far figure became a full day, one line per input
  provisional: boolean;
  history: { median: number | null; mad: number | null; p10: number | null; p90: number | null; samples: number; blocks: Array<{ start: string; end: string; value: number; z: number | null; methods: Record<string, AnomalyStatus> }> };
  band: { nearLow: number | null; nearHigh: number | null; outLow: number | null; outHigh: number | null };
  methods: Record<string, MethodResult>;
  peers: PeerAnomaly[];
  status: AnomalyStatus;
  direction: 'up' | 'down' | null;
  verdict?: Verdict; // the API's reading of a flagged row (rules.ts), the same on every tab
  slowRise?: boolean; // pipeline reads: near the edge from a slow climb on a row where that is fine (reads 'better')
  thinData?: boolean; // pipeline reads: too few to read, and far fewer than usual (the missing data is the anomaly)
  suddenRise?: boolean; // pipeline reads: a capped higher-is-better ratio that jumped out of range at once (still a problem)
  overCeiling?: boolean; // pipeline reads: a higher-is-better ratio above the level it should never pass (1 for platform per Triple Whale)
  sense?: 'up' | 'down' | 'either' | 'none'; // pipeline reads: which way is good ('either' = any move is a problem; 'none' = scale, neither)
  agree: number;
  consensus: { score: number | null; votes: Record<AnomalyStatus, number> };
  verify?: { sql: string; metabaseUrl: string } | null;
  trend: Trend | null; // null: too few points, or the metric has no read
  shift?: { date: string; before: number; after: number; z: number; status: 'out' | 'near' } | null; // pipeline reads: the day the level moved
  description?: string; // pipeline reads: what the check means, shown beside its name
  measurement?: boolean; // pipeline reads: a counting check (moves the card); false = business context, judged on Products & campaigns
  occurrence?: Occurrence | null; // pipeline reads: which flag this is, for explaining it until a new one starts
  contextEntries?: RowContext[]; // a flagged row: the findings entries (Not an issue / Snoozed) that apply to it now (same name as on card flags)
  drift?: { changeRel: number; days: number; mix: { mixRel: number; withinRel: number } | null; held?: boolean } | null; // pipeline reads: a counting ratio sliding steadily; mix = campaign-mix vs within-type split
}
export interface Totals { spend: number; revenue: number; profit: number; orders: number; clicks: number; visit_sessions: number; [k: string]: number }
export interface AnomalyReport {
  contextError?: string | null; // as OverviewReport
  subject: { grain: 'campaign' | 'product' | 'pipeline'; kind?: PipelineKind; key: string; label: string | null; store: string; channel: string | null; currency: string };
  track: AnomalyTrack;
  period: { start: string; end: string; days: number; projected: boolean };
  date: string;
  asOf: string;
  thresholds: Thresholds;
  historyDays: number;
  impact: DayImpact;
  elapsedShare: number | null;
  estimate: { elapsedHours: number; shares: { orders: number; spend: number }; aov: { value: number; source: 'today' | 'recent'; days: number } | null; soFar: Totals; projected: Totals | null; assumptions: string[] } | null;
  settlement: { ready: boolean; dates: number; sharesAtAge1: Record<string, number> } | null;
  provisional: boolean;
  missing: string[];
  metrics: MetricAnomaly[];
  series: Array<{ date: string; observed: boolean; inPeriod: boolean; projected: boolean; metrics: Record<string, number | null>; soFar?: Record<string, number | null> }>;
  peerSets: Array<{ set: string; key: string; days: number }>;
  assumptions: string[];
  summary: { out: number; near: number; in: number; insufficient: number };
}
export interface Thresholds { near: number; out: number }
export const THRESHOLD_PRESETS: Array<{ key: string; label: string; near: number; out: number; blurb: string }> = [
  { key: 'standard', label: 'Standard', near: 2, out: 3, blurb: 'flags more; a normal metric still lands near the edge about one day in 22. Every method loosens with it.' },
  { key: 'strict', label: 'Strict', near: 2.5, out: 4, blurb: 'the default; only the clearly unusual. Every method tightens with it.' },
];
// The fences and tails the sensitivity sets for the non-z methods (mirrors iqrFences / pctTails in the collector).
export const iqrFences = (th: { near: number; out: number }) => ({ near: th.near / 1.349 - 0.5, out: th.out / 1.349 - 0.5 });
export const pctTails = (th: { near: number; out: number }) => ({ near: Math.min(0.5, 0.10 * (2.5 / th.near) ** 2), out: Math.min(0.5, 0.025 * (4 / th.out) ** 2) });
export const DEFAULT_PRESET = 'strict';
export interface DayImpact { metric: 'profit_per_day' | 'net_profit_per_day' | 'revenue_per_day' | null; perDay: number | null; usual: number | null; currency: string; explain: string[] }
export const HISTORY_OPTIONS: Array<{ days: number; label: string }> = [{ days: 14, label: '2 weeks' }, { days: 30, label: '1 month' }, { days: 90, label: '3 months' }];
export const DEFAULT_HISTORY_DAYS = 30;
export const HISTORY_MIN = 7;
export const HISTORY_MAX = 90;
// Shared context (the collector's /findings/context store, domain `baselines`): what people and the
// hub's baselines-review agent know about the data. A cause entry says what is true and how it moves
// the data, scoped by tags, and leaves the flags in view for the agent to judge. A findings entry marks
// particular flags Not an issue or Snoozed, which takes them off the cards.
export const CONTEXT_DOMAIN = 'baselines';
export const CONTEXT_MAX_DAYS = 30; // a person's entry expires at most this many days out (Pacific)
export const CAUSE_DEFAULT_DAYS = 14;
export const SNOOZE_DAYS = 7;
export const CONTEXT_TEXT_MAX = 500;
export type FindingVerdict = 'not_issue' | 'snooze';
export const CAUSE_TAG_KINDS = ['store', 'channel', 'product', 'campaign', 'other'] as const;
export type CauseTagKind = (typeof CAUSE_TAG_KINDS)[number];
export type CauseTags = Partial<Record<CauseTagKind, string[]>>;
export interface ContextAuthor { type: 'person' | 'agent'; name: string; agent?: string; runId?: string }
/** One flagged metric a findings entry covers; key = `${store}|${subject}|${metric}`. */
export interface ContextFinding {
  key: string;
  label: string;
  context: { store: string; subject: string; subjectLabel: string | null; metric: string; metricLabel: string | null };
  occurrence: Occurrence | null;
  readThrough: string; // YYYY-MM-DD: the last day the report read when it was marked
}
export type ContextScope = { kind: 'cause'; tags: CauseTags } | { kind: 'findings'; verdict: FindingVerdict; findings: ContextFinding[] };
export interface ContextEntry {
  id: string;
  domain: string;
  statement: string;
  expect: string | null; // what it should do to the data
  scope: ContextScope;
  author: ContextAuthor;
  evidence: string | null;
  createdAt: string;
  updatedAt: string;
  expiresOn: string; // YYYY-MM-DD
  retiredAt: string | null;
  stale: { by: string; reason: string; at: string } | null; // the agent thinks it no longer holds
  history: Array<{ at: string; by: string; action: string; note: string | null }>;
  status: 'active' | 'expired';
}
export interface ContextInput { domain: string; statement: string; expect?: string | null; scope: ContextScope; author: ContextAuthor; evidence?: string | null; expiresOn: string }
export type ContextAction = 'edit' | 'extend' | 'retire' | 'clear_stale';
export interface ContextPatch { actor: ContextAuthor; action: ContextAction; changes?: Partial<{ statement: string; expect: string | null; scope: ContextScope; evidence: string | null; expiresOn: string }>; note?: string }
/** What a read carries on a flagged metric: the findings entries that apply to it now. */
export interface RowContext { id: string; verdict: FindingVerdict; statement: string; author: Pick<ContextAuthor, 'type' | 'name'>; expiresOn: string }

/** A pipeline flag's identity: a step is its shift day, a slide its direction, an ordinary flag its direction and the first day of its run (null = began before the window). */
export interface Occurrence { id: string; since: string | null }

/** The API's reading of a flagged row: worse, better, or volume (neither); null when not flagged. */
export type Verdict = 'worse' | 'better' | 'scale' | null;

export interface OverviewCard {
  key: string;
  grain: 'campaign' | 'product' | 'pipeline';
  kind?: PipelineKind; // pipeline cards: an ad channel in aggregate, the store, or one conversion action
  label: string;
  channel: string | null;
  family: string | null;
  currency: string;
  spend90: number;
  orders90: number;
  status: AnomalyStatus | 'missing' | 'better' | 'over'; // better = profit up with more good-way than bad-way flags; over = flagged only in the good direction (shown among the normal cards)
  missing: string[];
  summary: { out: number; near: number; in: number; insufficient: number };
  explained?: number; // flagged metrics marked Not an issue / Snoozed, left out of `flagged` and the status (set on the page, not by the API)
  flagged: Array<{ key: string; label: string; status: 'out' | 'near'; score: number | null; direction: 'up' | 'down' | null; verdict?: Verdict; better: boolean | null; profit?: boolean; minor?: boolean; thinData?: boolean; context?: boolean; edgeOnly?: boolean; value: number | null; median: number | null; unit: MetricAnomaly['unit']; occurrence?: Occurrence | null; contextEntries?: RowContext[] }>; // better null = spend / traffic (scale); contextEntries = findings entries that apply (named apart from the boolean `context`, a business row; the same name as on metric rows); context / edgeOnly = shown, but a tracking card does not move on it
  policy?: 'tracking' | 'campaign'; // which card policy the API decided the status by
  losing?: boolean; // a profit row below zero over the period
  provisional: boolean;
  projected: boolean;
  impact: DayImpact;
  stalled: string | null; // a campaign that stopped or collapsed while its usual day made money (May need attention): why
  chronic?: boolean; // products & campaigns: near only because it lost money again, as its usual day does (shown in its own section)
  // Profit (net profit for a product), orders and revenue per day over the period (today projected) with the usual median
  measures: Record<'profit' | 'orders' | 'revenue', { perDay: number | null; usual: number | null }>;
}
export interface OverviewReport {
  store: string;
  track: AnomalyTrack;
  period: { start: string; end: string; days: number; projected: boolean };
  date: string;
  asOf: string;
  fetchedAt: string;
  thresholds: Thresholds;
  historyDays: number;
  elapsedShare: number | null;
  cards: OverviewCard[];
  assumptions: string[];
  contextError?: string | null; // the shared context couldn't be read: every flag shows as unexplained
}
export interface SubjectOption { key: string; grain: 'campaign' | 'product'; label: string; store: string; channel: string | null; family: string | null; spend90: number; orders90: number }
export interface DayCurves {
  orders: { weekday: number[]; weekend: number[]; days: number };
  spend: { weekday: number[]; weekend: number[]; days: number; source: 'meta' | 'none' };
}
